import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { login } from '../../actions/auth';
import {
  Box,
  Button,
  Typography,
  Grid,
  CircularProgress,
  TextField,
  Alert
} from '@mui/material';
import styles from './login.module.scss';
import { authFieldSx } from './fieldStyles';
import MfaCodeForm from './MfaCodeForm';
import ReCAPTCHA from 'react-google-recaptcha';
import { setAlert } from '../../actions/alert';
import { useNavigate } from 'react-router-dom';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [captchaToken, setCaptchaToken] = useState(null);
  // Set when the password was right but the account needs an emailed code.
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY

  const [loginError, setLoginError] = useState({ email: '', password: '' });
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const { loading } = useSelector(state => state.auth);
  const onChange = (e) => {
    setLoginError({ ...loginError, [e.target.name]: '' });
    setFormData({ ...formData, [e.target.name]: e.target.value });
  }

  const onSubmit = async e => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    let errors = {};

    if (!emailRegex.test(email) || !email) {
      errors.email = 'Invalid email';
    }
    if (!password) {
      errors.password = 'Invalid password';
    }
    if (Object.keys(errors).length > 0) {
      setLoginError(errors);
      return;
    }

    // If failedAttempts >= 2 and no captchaToken, require captcha
    if (failedAttempts >= 2 && !captchaToken) {
      dispatch(setAlert('Please complete the reCAPTCHA before logging in', 'error'));
      return;
    }

    // If captchaToken is present, mark when it was solved and reset failedAttempts
    if (captchaToken) {
      setFailedAttempts(0);
    }

    try {
      const result = await dispatch(login({ username: email, email, password, recaptcha: captchaToken }))
      setCaptchaToken(null); // Clear captcha on success
      setFailedAttempts(0);  // Reset failed attempts on success
      if (result?.mfaRequired) {
        setMfaChallenge(result);
        return;
      }
      navigate('/home');
    } catch {
      // If captcha was just solved, start counting failed attempts again
      if (captchaToken) {
        setCaptchaToken(null); // Require new captcha after next 2 fails
        setFailedAttempts(1);
      } else {
        setFailedAttempts(prev => prev + 1);
      }
    }
  };


  const { email, password } = formData;

  const items = [
    {
      id: 0,
      name: 'email',
      label: 'Email',
      value: email,
    },
    {
      id: 1,
      name: 'password',
      label: 'Password',
      value: password,
    },
  ];

  return (
    <Grid className={`${styles.login}`} container
      sx={{
        width: '100%',
        height: '100vh',
        flexDirection: 'column',
        overflow: 'hidden', // Add this line
        position: 'relative', // Ensure stacking context for absolute children
      }}
    >
      {/* Navbar */}
      <Box className={styles.navigation_outer}>
        <Box className={styles.navigation}>

          <Box className={`${styles.nav_50} ${styles.left}`}>
            <Box
              component="a"
              data-w-id="07966884-9627-224b-36fb-c720d7dc64ee"
              href="/"
              aria-current="page"
              className={`${styles.nav_logo} w-inline-block w--current`}
              sx={{ textDecoration: 'none' }}
            >
              <Box className={styles.nav_logo_image} />
              <Box style={{ opacity: 0 }} className={styles.link_inter}>
                <Box style={{ width: '1px', height: '0%' }} className={styles.left_line} />
                <Box style={{ width: '1px', height: '0%' }} className={styles.right_line} />
                <Box style={{ width: '0%', height: '1px' }} className={styles.bottom_line} />
                <Box style={{ width: '0%', height: '1px' }} className={styles.top_line} />
              </Box>
            </Box>
            <Box className={styles.nav_progress_outer}>
              <Box className={styles.nav_logo_text}>
                CHARR{'\u00A0\u00A0'}APP
              </Box>
              <Box
                className={styles.progress_meter}
                sx={{ willChange: 'width, height', width: '0%', height: '100%' }}
              />
            </Box>
          </Box>
          <Box className={`${styles.nav_50} ${styles.outline}`}>
            {/* <Box component="a" href="/about-us" className="footer-link hide w-inline-block"><Box>About</Box></Box>
  <Box component="a" href="/projects" className="footer-link hide w-inline-block"><Box>Projects</Box></Box> */}
            <Box
              component="a"
              href="https://mycharr.earth"
              target="_blank"
              rel="noopener noreferrer"
              className={`${styles['glowing-wrapper']} ${styles['glowing-wrapper-active']} ${styles.menu}`}
              sx={{ textDecoration: 'none' }}
            >
              <Box className={styles['glowing-wrapper-animations']}>
                <Box className={styles['glowing-wrapper-glow']} />
                <Box className={styles['glowing-wrapper-mask-wrapper']}>
                  <Box className={styles['glowing-wrapper-mask']} />
                </Box>
              </Box>
              <Box className={`${styles['glowing-wrapper-borders-masker']} ${styles.menu}`}>
                <Box className={styles['glowing-wrapper-borders']} />
              </Box>
              <Box className={`${styles['glowing-wrapper-button']} ${styles.menu}`}>
                <Box className={styles['navbar_button'] + ' ' + styles.resources}>
                  <Typography className={styles['navbar_button-text']}>Website</Typography>
                </Box>
                <Box className={styles['button-mesh']}>
                  <Box className={styles['button__inner']}>
                    <Box className={styles.gradient}>
                      <Box className={`${styles['gradient__ball']} ${styles['gradient__ball--1']}`} />
                      <Box className={`${styles['gradient__ball']} ${styles['gradient__ball--2']}`} />
                    </Box>
                    <Box
                      component="img"
                      src={'charr-logo-login.webp'}
                      alt=""
                      className={styles.button_svg}
                    />
                  </Box>
                  <Box className={styles['gradient-glow']}>
                    <Box className={`${styles['gradient__ball']} ${styles['gradient__ball--1']}`} />
                    <Box className={`${styles['gradient__ball']} ${styles['gradient__ball--2']}`} />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Grid item xs={12} sx={{ background: "none", height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        {mfaChallenge ? (
          <MfaCodeForm
            challenge={mfaChallenge}
            onVerified={() => navigate('/home')}
            onRestart={() => {
              setMfaChallenge(null);
              setFormData({ ...formData, password: '' });
            }}
          />
        ) : (
        <Box
          component='form'
          onSubmit={e => onSubmit(e)}
          className={styles.login_form}
        >
          {/* <Typography variant="h5" className="heading-5" sx={{ marginRight: 'auto' }}>Login</Typography> */}
          {items.map(item => {
            return (
              <Box component='div' sx={{ mt: 2, width: '250px' }} key={item.id}>
                <TextField
                  placeholder={item.label}
                  name={item.name}
                  value={item.value}
                  type={item.name === 'password' ? 'password' : 'text'}
                  sx={authFieldSx}
                  onChange={onChange}
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: false }}
                />
                {loginError[item.name] ? <Alert
                  severity='error'
                  sx={{ mt: 1, fontSize: '0.8rem' }}
                >
                  {loginError[item.name]}
                </Alert> : null}
              </Box>
            );
          })}

          <Button
            className={`${styles['glowing-wrapper']} ${styles['glowing-wrapper-active']} ${styles.menu}`}
            sx={{ textDecoration: 'none', my: 2, width: '255px' }}
            type='submit'
            disabled={loading}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center' }} className={styles['navbar_button'] + ' ' + styles.resources}>
              {loading ? (
                <CircularProgress sx={{ color: '#fff' }} />
              ) : (
                <Typography className={styles['navbar_button-text']}>Login</Typography>
              )}
            </Box>
          </Button>
          {failedAttempts >= 2 && (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  transform: 'scale(0.83)',
                  transformOrigin: 'center',
                }}
              >
                <ReCAPTCHA
                  sitekey={SITE_KEY}
                  onChange={(token) => setCaptchaToken(token)}
                />
              </Box>
            </Box>
          )}
        </Box>
        )}
      </Grid>
      {/* Main content can go here */}
      <Box className={styles['home-lights']}>
        <Box
          component="img"
          width="1440"
          alt=""
          src={'public-purple-glimmer-effect.webp'}
          className={styles["light-left"]}
        />
      </Box>
    </Grid>
  );
};

export default Login;